import { bracketDepth } from "@/lib/tool-kit/style-checkers/a-words";
import {
  countWords,
  headingFlags,
  insideAny,
  quotedSpans,
  type Span,
  splitSentences,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Latin abbreviations in academic prose: e.g., i.e., etc., cf., viz., vs. and et al.
 *
 * What the manuals say (the page cites them; verify against your edition):
 * - APA 7 (Publication Manual §6.29): use cf., e.g., etc., i.e., viz. and vs. only inside
 *   parentheses; in running text write the English: "for example", "that is", "and so
 *   forth", "compare", "namely", "versus". Punctuate the abbreviation as if the English
 *   were spelled out, so "e.g.," and "i.e.," take a comma. Do not italicize or define them.
 *   "et al." is the exception: it belongs in text and parenthetical citations.
 * - Chicago 18 also keeps e.g. and i.e. for parentheses and notes and prefers English in
 *   running text; the checker treats that as advice ("possible"), not a rule.
 *
 * Findings are of three kinds: punctuation (periods, the comma after e.g./i.e., "et. al."),
 * placement (an abbreviation outside parentheses) and redundancy ("and etc.", "such as …
 * etc."). Brackets are balanced within a sentence, so an opening bracket in an earlier
 * sentence is not seen.
 */

export type AbbreviationKey =
  | "e.g."
  | "i.e."
  | "etc."
  | "cf."
  | "viz."
  | "vs."
  | "et al.";

export type StyleChoice = "apa" | "chicago" | "punctuation";

export type IssueKind =
  | "periods"
  | "comma"
  | "double-period"
  | "placement"
  | "redundant"
  | "list-comma";

export interface AbbreviationIssue {
  kind: IssueKind;
  /** True when the style treats this as a rule; false when it is advice. */
  firm: boolean;
}

export interface AbbreviationMatch extends Span {
  /** As written, with a following comma if one is attached. */
  text: string;
  key: AbbreviationKey;
  insideParentheses: boolean;
  issues: AbbreviationIssue[];
  /** The text to put in the span's place, or null when it needs no change. */
  suggestion: string | null;
  sentenceStart: number;
  sentence: string;
}

export interface AbbreviationRow {
  key: AbbreviationKey;
  count: number;
  inside: number;
  outside: number;
}

export interface AbbreviationReport {
  words: number;
  sentences: number;
  matches: AbbreviationMatch[];
  /** Matches with at least one issue. */
  flagged: number;
  rows: AbbreviationRow[];
  byIssue: Record<IssueKind, number>;
}

/* ------------------------------------------------------------------ patterns */

/**
 * One abbreviation as written. Lowercase forms may lose their periods; the capitalized
 * sentence-start forms must keep them, so "CF" (cystic fibrosis) and "Vs" are left alone.
 * "eg" and "ie" count only when a comma follows, which is how the typo looks.
 */
const RE_ABBREVIATION = new RegExp(
  [
    "(?<![\\p{L}\\p{N}./@_-])(?:",
    "[eE]\\.\\s?g\\.?|eg(?:\\.|(?=,))|[iI]\\.\\s?e\\.?|ie(?:\\.|(?=,))|",
    "(?:etc|cf|viz|vs)\\.?|(?:Etc|Cf|Viz|Vs)\\.|",
    "et\\.?\\s*al\\.?",
    ")(?![\\p{L}\\p{N}@_])",
  ].join(""),
  "gu"
);

const RE_NON_LETTERS = /[^a-z]/g;
const RE_AFTER_SENTENCE_END = /^\s*(?:$|\n|\p{Lu})/u;
const RE_LAST_LIST_ITEM = /,\s*([^,.;:()]{1,40})$/;
const RE_LEADING_AND = /^and\s+/;
const RE_AND_BEFORE = /\band\s+$/i;
const RE_EXEMPLIFIER = /\b(?:such as|for example|including|e\.g\.?,?)\s/i;
const RE_DOUBLE_PERIOD = /^\.(?!\.)/;
const RE_TRAILING_SPACE = /\s+$/;
const RE_WORD_SPLIT = /\s+/;
const RE_UPPER_INITIAL = /^\p{Lu}/u;
const RE_LEADING_WORD = /^\s*(\p{L}+)/u;
const RE_TRAILING_WORD = /(\p{L}+)\s*$/u;

function keyOf(written: string): AbbreviationKey {
  const letters = written.toLowerCase().replace(RE_NON_LETTERS, "");
  switch (letters) {
    case "eg":
      return "e.g.";
    case "ie":
      return "i.e.";
    case "etal":
      return "et al.";
    case "etc":
      return "etc.";
    case "cf":
      return "cf.";
    case "viz":
      return "viz.";
    default:
      return "vs.";
  }
}

/** Needs a comma after it, punctuated as if "for example" / "that is" / "namely". */
function takesComma(key: AbbreviationKey): boolean {
  return key === "e.g." || key === "i.e." || key === "viz.";
}

/** The English that stands in for each abbreviation in running text. */
const ENGLISH: Record<
  Exclude<AbbreviationKey, "et al.">,
  { apa: string; chicago: string }
> = {
  "e.g.": { apa: "for example", chicago: "for example" },
  "i.e.": { apa: "that is", chicago: "that is" },
  "etc.": { apa: "and so forth", chicago: "and so on" },
  "cf.": { apa: "compare", chicago: "compare" },
  "viz.": { apa: "namely", chicago: "namely" },
  "vs.": { apa: "versus", chicago: "versus" },
};

/* ------------------------------------------------------------------ analysis */

interface Raw {
  start: number;
  end: number;
  written: string;
  key: AbbreviationKey;
}

function rawMatches(text: string): Raw[] {
  return [...text.matchAll(RE_ABBREVIATION)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
    written: match[0],
    key: keyOf(match[0]),
  }));
}

function endsSentence(text: string, end: number): boolean {
  return RE_AFTER_SENTENCE_END.test(text.slice(end, end + 3));
}

function punctuationIssues(
  text: string,
  raw: Raw,
  commaFollows: boolean
): AbbreviationIssue[] {
  const issues: AbbreviationIssue[] = [];
  if (raw.written.toLowerCase() !== raw.key && !looksLikeTitle(text, raw)) {
    issues.push({ kind: "periods", firm: true });
  }
  if (takesComma(raw.key) && !commaFollows && text[raw.end] !== ")") {
    issues.push({ kind: "comma", firm: true });
  }
  if (
    raw.written.endsWith(".") &&
    RE_DOUBLE_PERIOD.test(text.slice(raw.end, raw.end + 2))
  ) {
    issues.push({ kind: "double-period", firm: true });
  }
  return issues;
}

/** "apples, pears etc." — a short list item after a comma, and no comma before etc. */
function missingListComma(before: string): boolean {
  if (before.replace(RE_TRAILING_SPACE, "").endsWith(",")) {
    return false;
  }
  const item = RE_LAST_LIST_ITEM.exec(before.replace(RE_TRAILING_SPACE, ""));
  return item ? item[1].trim().split(RE_WORD_SPLIT).length <= 3 : false;
}

function redundancyIssues(raw: Raw, before: string): AbbreviationIssue[] {
  if (raw.key !== "etc.") {
    return [];
  }
  const issues: AbbreviationIssue[] = [];
  const andBefore = RE_AND_BEFORE.test(before);
  if (andBefore) {
    issues.push({ kind: "redundant", firm: true });
  } else if (RE_EXEMPLIFIER.test(before)) {
    issues.push({ kind: "redundant", firm: false });
  }
  if (!andBefore && missingListComma(before)) {
    issues.push({ kind: "list-comma", firm: false });
  }
  return issues;
}

/** "Cats vs. Dogs": between two capitalized words, vs. is part of a title. */
function looksLikeTitle(text: string, raw: Raw): boolean {
  if (raw.key !== "vs.") {
    return false;
  }
  const previous =
    RE_TRAILING_WORD.exec(
      text.slice(Math.max(0, raw.start - 30), raw.start)
    )?.[1] ?? "";
  const next =
    RE_LEADING_WORD.exec(text.slice(raw.end, raw.end + 30))?.[1] ?? "";
  return RE_UPPER_INITIAL.test(previous) && RE_UPPER_INITIAL.test(next);
}

function placementIssue(
  text: string,
  raw: Raw,
  inside: boolean,
  style: StyleChoice
): AbbreviationIssue[] {
  if (
    raw.key === "et al." ||
    inside ||
    style === "punctuation" ||
    looksLikeTitle(text, raw)
  ) {
    return [];
  }
  return [{ kind: "placement", firm: style === "apa" }];
}

/**
 * The text to put where the abbreviation (and its comma) stands. Outside parentheses it is
 * the English equivalent; inside, the standard abbreviation with its comma.
 */
function suggest(
  text: string,
  raw: Raw,
  issues: readonly AbbreviationIssue[],
  style: StyleChoice,
  commaFollows: boolean
): string | null {
  if (issues.length === 0) {
    return null;
  }
  const { key } = raw;
  const placement = issues.some((issue) => issue.kind === "placement");
  if (placement && key !== "et al.") {
    // "pears, and etc." → "pears, and so forth": the "and" is already there.
    const andBefore = issues.some(
      (issue) => issue.kind === "redundant" && issue.firm
    );
    const full = ENGLISH[key][style === "chicago" ? "chicago" : "apa"];
    const english = andBefore ? full.replace(RE_LEADING_AND, "") : full;
    const tail = takesComma(key) ? "," : "";
    const stop =
      key === "etc." && endsSentence(text, raw.end + (commaFollows ? 1 : 0))
        ? "."
        : "";
    return `${english}${tail}${stop}`;
  }
  const comma = takesComma(key) ? "," : "";
  return `${key}${comma}`;
}

const ISSUE_KINDS: readonly IssueKind[] = [
  "periods",
  "comma",
  "double-period",
  "placement",
  "redundant",
  "list-comma",
];

export interface AbbreviationOptions {
  style?: StyleChoice;
  /** Skip abbreviations inside quotation marks. Default true. */
  ignoreQuotes?: boolean;
}

export function checkLatinAbbreviations(
  text: string,
  options: AbbreviationOptions = {}
): AbbreviationReport {
  const style = options.style ?? "apa";
  const quotes = options.ignoreQuotes === false ? [] : quotedSpans(text);
  const sentences = splitSentences(text);
  const headings = headingFlags(sentences);
  const matches: AbbreviationMatch[] = [];
  let counted = 0;
  const raws = rawMatches(text);
  let cursor = 0;
  for (const [i, sentence] of sentences.entries()) {
    if (headings[i]) {
      continue;
    }
    counted++;
    while (cursor < raws.length && raws[cursor].start < sentence.end) {
      const raw = raws[cursor++];
      if (raw.start < sentence.start || insideAny(quotes, raw.start, raw.end)) {
        continue;
      }
      matches.push(analyse(text, raw, sentence, style));
    }
  }
  return report(text, counted, matches);
}

function analyse(
  text: string,
  raw: Raw,
  sentence: { start: number; text: string },
  style: StyleChoice
): AbbreviationMatch {
  const commaFollows = text[raw.end] === "," && takesComma(raw.key);
  const before = text.slice(sentence.start, raw.start);
  const inside = bracketDepth(text, sentence.start, raw.start) > 0;
  const issues = [
    ...punctuationIssues(text, raw, commaFollows),
    ...placementIssue(text, raw, inside, style),
    ...redundancyIssues(raw, before),
  ];
  const doubled = issues.some((issue) => issue.kind === "double-period");
  const end = raw.end + (commaFollows || doubled ? 1 : 0);
  return {
    start: raw.start,
    end,
    text: text.slice(raw.start, end),
    key: raw.key,
    insideParentheses: inside,
    issues,
    suggestion: suggest(text, raw, issues, style, commaFollows),
    sentenceStart: sentence.start,
    sentence: sentence.text,
  };
}

function report(
  text: string,
  sentences: number,
  matches: AbbreviationMatch[]
): AbbreviationReport {
  const byIssue = Object.fromEntries(
    ISSUE_KINDS.map((kind) => [kind, 0])
  ) as Record<IssueKind, number>;
  const rows = new Map<AbbreviationKey, AbbreviationRow>();
  for (const match of matches) {
    for (const issue of match.issues) {
      byIssue[issue.kind]++;
    }
    const row = rows.get(match.key) ?? {
      key: match.key,
      count: 0,
      inside: 0,
      outside: 0,
    };
    row.count++;
    row[match.insideParentheses ? "inside" : "outside"]++;
    rows.set(match.key, row);
  }
  return {
    words: countWords(text),
    sentences,
    matches,
    flagged: matches.filter((m) => m.issues.length > 0).length,
    rows: [...rows.values()].sort(
      (a, b) => b.count - a.count || a.key.localeCompare(b.key)
    ),
    byIssue,
  };
}

/** The English that replaces an abbreviation in running text; et al. has none. */
export function englishFor(key: AbbreviationKey, style: StyleChoice): string {
  if (key === "et al.") {
    return key;
  }
  return ENGLISH[key][style === "chicago" ? "chicago" : "apa"];
}
